// Helper functions for discount integration

/**
 * Transform cart data from checkout pages to format expected by discount API
 * @param {Array} productList - The cart product list from localStorage
 * @returns {Array} - Formatted cart items for discount API
 */
export const transformCartDataForDiscount = (productList) => {
  if (!productList || productList.length < 4) {
    console.log("Invalid productList structure:", productList);
    return [];
  }

  const [identifiers, quantities, products, prices] = productList;
  console.log("Raw product data:", products);

  return identifiers.map((identifier, index) => {
    const [product_id, color_id, size_id] = identifier.split("_");
    const product = products[index];

    console.log(`Processing product ${index}:`, product);
    console.log(`Product ${index} raw data:`, {
      product_id,
      color_id,
      size_id,
      product: product[0],
      available_fields: Object.keys(product[0] || {}),
    });

    // Handle different possible field names for category and subcategory
    // Ensure they are converted to numbers
    const category_id = parseInt(
      product[0]?.category_id ||
        product[0]?.p_cat ||
        product[0]?.product_cat ||
        0
    );
    const subcategory_id = parseInt(
      product[0]?.subcategory_id ||
        product[0]?.p_subcat ||
        product[0]?.product_sub_cat ||
        0
    );

    console.log(
      `Product ${product_id} - category_id: ${category_id}, subcategory_id: ${subcategory_id}`
    );
    console.log(`Product ${product_id} - raw category fields:`, {
      category_id_raw: product[0]?.category_id,
      p_cat_raw: product[0]?.p_cat,
      product_cat_raw: product[0]?.product_cat,
      subcategory_id_raw: product[0]?.subcategory_id,
      p_subcat_raw: product[0]?.p_subcat,
      product_sub_cat_raw: product[0]?.product_sub_cat,
    });

    return {
      product_id: parseInt(product_id),
      color_id: parseInt(color_id),
      size_id: size_id === "0" ? null : parseInt(size_id), // Handle no-size products
      quantity: parseInt(quantities[index]),
      price: parseFloat(prices[index]) / parseInt(quantities[index]), // Unit price
      total_price: parseFloat(prices[index]), // Total price for this line item
      // Include additional product info that might be needed for category/subcategory discounts
      product_name: product[0]?.p_name || "",
      category_id: category_id,
      subcategory_id: subcategory_id,
      // Also include alternative field names for compatibility
      p_cat: category_id,
      p_subcat: subcategory_id,
    };
  });
};

/**
 * Calculate subtotal from product list
 * @param {Array} productList - The cart product list from localStorage
 * @returns {number} - Calculated subtotal
 */
export const calculateSubtotal = (productList) => {
  if (
    !productList ||
    productList.length < 4 ||
    !Array.isArray(productList[3])
  ) {
    return 0;
  }

  return productList[3].reduce((acc, price) => {
    return acc + (parseFloat(price) || 0);
  }, 0);
};

/**
 * Calculate final total including shipping and discount
 * Note: Discount is applied to subtotal only, not to shipping
 * @param {number} subtotal - Cart subtotal
 * @param {number} shippingFee - Shipping fee
 * @param {Object} appliedDiscount - Applied discount object
 * @returns {Object} - Object with breakdown of totals
 */
export const calculateFinalTotal = (
  subtotal,
  shippingFee = 0,
  appliedDiscount = null
) => {
  const shipping = parseFloat(shippingFee) || 0;
  const discount = appliedDiscount?.amount || 0;

  // Ensure discount doesn't exceed subtotal
  const actualDiscount = Math.min(discount, subtotal);

  return {
    subtotal: subtotal,
    shipping: shipping,
    discount: actualDiscount,
    discountedSubtotal: subtotal - actualDiscount,
    total: subtotal - actualDiscount + shipping,
  };
};

/**
 * Update payment option prices with discount applied
 * @param {Array} paymentOptions - Original payment options
 * @param {number} baseTotal - Base total before payment method fees
 * @returns {Array} - Updated payment options with correct pricing
 */
export const updatePaymentOptionsWithDiscount = (paymentOptions, baseTotal) => {
  return paymentOptions.map((option) => {
    let newPrice;

    switch (option.id) {
      case "COD":
      case "BANK":
        newPrice = baseTotal;
        break;
      case "PAYHERE":
        newPrice = +(baseTotal * 1.033).toFixed(2);
        break;
      case "KOKO":
        newPrice = +(baseTotal * 1.11).toFixed(2);
        const newInstallment =
          newPrice > 0
            ? `LKR ${(newPrice / 3).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} x 3 months`
            : undefined;
        return {
          ...option,
          price: newPrice,
          installment: newInstallment,
        };
      default:
        newPrice = baseTotal;
    }

    return {
      ...option,
      price: newPrice,
    };
  });
};

/**
 * Format discount data for order submission
 * @param {Object} appliedDiscount - Applied discount object
 * @returns {Object} - Formatted discount data for order
 */
export const formatDiscountForOrder = (appliedDiscount) => {
  if (!appliedDiscount) {
    return null;
  }

  return {
    discount_id: appliedDiscount.discountId,
    discount_code: appliedDiscount.code,
    discount_amount: appliedDiscount.amount,
    original_total: appliedDiscount.originalTotal,
    final_total: appliedDiscount.finalTotal,
  };
};
